import { saveAs } from "file-saver";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import { TOAST_MESSAGES } from "~/constants/messages";
import { DocumentApi } from "~/functions/api/document";
import { useProjectMembers } from "~/hooks/useProjectMembers";
import ExportPrimary from "~/images/export_primary.svg?react";
import type { Document } from "~/interface/document";
import type { AuthState } from "~/slices/auth";
import {
  cancelEdit,
  clearError,
  initializeCostEffectiveness,
  setPersonPriceMonthPrice,
  setPostImplementationMonthlyCost,
  setPreImplementationMonthlyCost,
  toggleEditMode,
  updateMonthlyCosts,
  updateTextsFromCostEffectivenessData,
} from "~/slices/costEffectivenessSlice";
import type { AppDispatch, RootState } from "~/store";
import { calculateTotalWorkloadsFromUsecaseTable } from "~/utils/estimation";
import styles from "./index.module.scss";

// ROIデータの型定義
interface RoiDataPoint {
  period: string;
  initialCost: number;
  operationalCost: number;
  benefit: number;
  cumulativeCost: number;
  cumulativeBenefit: number;
}

/**
 * 月運用コストと初期費用をもとに動的にROIデータを生成する関数
 * @param monthlyOperationalCostYen 月運用コスト（円単位）
 * @param initialCostYen 初期費用（円単位）
 * @returns ROIデータの配列
 */
const generateRoiData = (
  monthlyOperationalCostYen: number,
  initialCostYen: number = 50000000,
): RoiDataPoint[] => {
  // コメントを修正: 初期費用、運用コストは円単位で受け取り、円単位で計算
  const data: RoiDataPoint[] = [];

  // 導入時のデータ (円単位で記録)
  data.push({
    period: "導入時",
    initialCost: initialCostYen, // 円単位
    operationalCost: 0,
    benefit: 0, // Benefitも円単位で計算するように変更が必要な場合は修正
    cumulativeCost: initialCostYen, // 円単位
    cumulativeBenefit: 0,
  });

  // 累積コストも円単位で計算
  let cumulativeCost = initialCostYen;
  let cumulativeBenefit = 0;

  // 1ヶ月目から12ヶ月目までのデータを生成
  for (let month = 1; month <= 12; month++) {
    // 月ごとの効果 (Benefit) の単位も円と仮定 (必要なら調整)
    // 例: 500万円から増加と仮定 -> 5,000,000円から増加と仮定
    const benefit = Math.floor(5000000 * 1.3 ** (month - 1));

    // 累積コストと累積効果を計算 (円単位)
    cumulativeCost += monthlyOperationalCostYen; // 月次運用コストも円単位で加算
    cumulativeBenefit += benefit;

    data.push({
      period: `${month}ヶ月目`,
      initialCost: 0, // 2ヶ月目以降の初期費用は0
      operationalCost: monthlyOperationalCostYen, // 円単位
      benefit: benefit, // 円単位
      cumulativeCost: cumulativeCost, // 円単位
      cumulativeBenefit: cumulativeBenefit, // 円単位
    });
  }

  return data;
};

interface CalculatedDataPoint {
  period: string;
  month: number;
  cumulativeCostAfter: number;
  cumulativeCostBefore: number;
  cumulativeEffect: number;
}

/**
 * 損益分岐点の月数を計算し、新しいデータを生成する関数
 * @param preImplementationCostYen 導入前の月運用コスト (円単位)
 * @param postImplementationCostYen 導入後の月運用コスト (円単位)
 * @param initialCostYen 初期費用 (円単位)
 * @returns 損益分岐点と表示用データ
 */
const calculateBreakEvenMonthsAndData = (
  preImplementationCostYen: number,
  postImplementationCostYen: number,
  initialCostYen: number,
) => {
  const dynamicRoiData = generateRoiData(postImplementationCostYen, initialCostYen);

  const initialInvestmentYen = dynamicRoiData[0]!.cumulativeCost;
  const monthlyOperationalCostAfterYen = dynamicRoiData[1]!.operationalCost;
  const monthlyOperationalCostBeforeYen = preImplementationCostYen;

  let breakEvenMonth = 0;
  let cumulativeCostAfterYen = initialInvestmentYen;
  let cumulativeCostBeforeYen = 0;
  let cumulativeEffectYen = -initialInvestmentYen;

  const calculatedData: CalculatedDataPoint[] = [];

  for (let month = 0; month <= 60; month++) {
    if (month === 0) {
      cumulativeCostAfterYen = initialInvestmentYen;
      cumulativeCostBeforeYen = 0;
      cumulativeEffectYen = -initialInvestmentYen;

      calculatedData.push({
        period: "導入時",
        month: 0,
        cumulativeCostAfter: cumulativeCostAfterYen,
        cumulativeCostBefore: cumulativeCostBeforeYen,
        cumulativeEffect: cumulativeEffectYen,
      });
    } else {
      cumulativeCostAfterYen += monthlyOperationalCostAfterYen;
      cumulativeCostBeforeYen += monthlyOperationalCostBeforeYen;
      cumulativeEffectYen = cumulativeCostBeforeYen - cumulativeCostAfterYen;

      calculatedData.push({
        period: `${month}ヶ月目`,
        month,
        cumulativeCostAfter: cumulativeCostAfterYen,
        cumulativeCostBefore: cumulativeCostBeforeYen,
        cumulativeEffect: cumulativeEffectYen,
      });

      // 損益分岐点がまだ見つかっていなくて、累積効果がプラスに転じた場合
      if (breakEvenMonth === 0 && cumulativeEffectYen >= 0) {
        breakEvenMonth = month;
      }
    }
  }

  const displayMonths = breakEvenMonth > 0 ? Math.max(breakEvenMonth * 2, 12) : 60;

  const displayData = calculatedData.slice(0, displayMonths + 1);

  return {
    breakEvenMonth: breakEvenMonth > 0 ? `${breakEvenMonth}ヶ月目` : "60ヶ月以上",
    data: displayData,
    maxDisplayMonth: displayMonths,
  };
};

const CostEffectiveness: React.FC = () => {
  const { currentUser, currentProject, organization } = useSelector(
    (state: RootState) => state.auth as AuthState,
  );
  const {
    preImplementationMonthlyCostYen,
    postImplementationMonthlyCostYen,
    personPriceMonthPriceYen,
    preImplementationMonthlyCostText,
    postImplementationMonthlyCostText,
    personPriceMonthPriceText,
    initialCostYen,
    isEditing,
    isPreImplementationMonthlyCostInvalid,
    isPostImplementationMonthlyCostInvalid,
    isPricePerMonthInvalid,
    error,
  } = useSelector((state: RootState) => state.costEffectiveness);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { error: projectError } = useProjectMembers();

  const [estimationInfo, setEstimationInfo] = useState<Document | null>(null);
  const [totalWorkload, setTotalWorkload] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [highlightedStep, setHighlightedStep] = useState<number | null>(null);
  const [chartSize, setChartSize] = useState({ width: 1200, height: 400 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isSliceLoading, setIsSliceLoading] = useState(true);

  useEffect(() => {
    if (!currentProject) {
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    if (!currentProject.outputs?.["estimation"]?.id) {
      toast.error(TOAST_MESSAGES.COST_EFFECTIVENESS.ESTIMATION_NOT_GENERATED);
      navigate(`/${organization.id}/project/${currentProject.id}/dashboard`);
      return;
    }

    if (!preImplementationMonthlyCostYen || !postImplementationMonthlyCostYen) {
      setIsFirstTimeUser(true);
    }

    const initSlice = async () => {
      setIsSliceLoading(true);
      try {
        if (currentProject.costEffectivenessData) {
          await dispatch(initializeCostEffectiveness()).unwrap();
          if (!isEditing) {
            await dispatch(updateTextsFromCostEffectivenessData()).unwrap();
          }
        }
        setHasInitialized(true);
      } finally {
        setIsSliceLoading(false);
      }
    };

    if (!hasInitialized) {
      void initSlice();
    }
  }, [
    currentProject,
    navigate,
    organization.id,
    currentUser.organization_id,
    dispatch,
    hasInitialized,
    isEditing,
    preImplementationMonthlyCostYen,
    postImplementationMonthlyCostYen,
  ]);

  useEffect(() => {
    if (!isEditing && hasInitialized && currentProject?.costEffectivenessData) {
      dispatch(updateTextsFromCostEffectivenessData());
    }
  }, [isEditing, hasInitialized, dispatch, currentProject?.costEffectivenessData]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (projectError) {
      console.error("Project data loading error:", projectError);
    }
  }, [projectError]);

  // Fetch document info for header
  useEffect(() => {
    const fetchDocumentInfo = async () => {
      if (!currentProject?.outputs?.["estimation"]?.id) {
        setIsDataLoading(false);
        return;
      }

      try {
        setIsDataLoading(true);
        const documentApi = new DocumentApi();
        const response = await documentApi.get(
          currentUser.organization_id,
          currentProject.id,
          currentProject.outputs?.["estimation"]?.id,
        );
        if (response) {
          setEstimationInfo(response);
        }
        // estimation ドキュメントの内容から合計工数を算出（要件定義・設計 +0.8 を含む）
        if (response?.content) {
          try {
            const parsed = JSON.parse(response.content);
            const primary = calculateTotalWorkloadsFromUsecaseTable(parsed);
            const total = Math.round((primary + 0.8) * 100) / 100;
            setTotalWorkload(total);
          } catch (e) {
            console.error("Failed to parse estimation content for total workload:", e);
            setTotalWorkload(0);
          }
        }
      } catch (error) {
        console.error("Failed to fetch document info:", error);
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchDocumentInfo();
  }, [currentProject, currentUser]);

  // チャート参照用のref
  const chartRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const UpdateMonthlyCosts = async () => {
    const result = await dispatch(updateMonthlyCosts());
    if (updateMonthlyCosts.fulfilled.match(result)) {
      toast.success(TOAST_MESSAGES.COST_EFFECTIVENESS.UPDATE_SUCCESS);
    }
  };

  // PNG画像をダウンロードする関数
  const handleDownload = useCallback(async () => {
    try {
      setIsExporting(true);

      // チャート内のSVG要素を直接取得
      if (!chartRef.current) {
        toast.error(TOAST_MESSAGES.COST_EFFECTIVENESS.CHART_NOT_FOUND);
        return;
      }

      // .recharts-wrapperクラスのSVG要素を探す
      const svgElement = chartRef.current.querySelector(".recharts-wrapper svg");
      if (!svgElement) {
        toast.error(TOAST_MESSAGES.COST_EFFECTIVENESS.SVG_NOT_FOUND);
        return;
      }

      // SVGをシリアライズ
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const svgUrl = URL.createObjectURL(svgBlob);

      // Canvasを作成してSVGを描画
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // SVGの元のサイズを使用
        canvas.width = svgElement.clientWidth || 1200;
        canvas.height = svgElement.clientHeight || 400;

        // 白背景を設定
        if (ctx) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);

          // PNGとして保存
          canvas.toBlob((blob) => {
            if (blob) {
              saveAs(blob, `${currentProject.name}_費用対効果グラフ.png`);
              toast.success(TOAST_MESSAGES.COST_EFFECTIVENESS.EXPORT_SUCCESS);
            }
            URL.revokeObjectURL(svgUrl);
            setIsExporting(false);
          }, "image/png");
        }
      };

      img.onerror = () => {
        toast.error(TOAST_MESSAGES.COST_EFFECTIVENESS.IMAGE_GENERATION_FAILED);
        URL.revokeObjectURL(svgUrl);
        setIsExporting(false);
      };

      img.src = svgUrl;
    } catch (error) {
      console.error("Export error:", error);
      toast.error(
        `エクスポート処理中にエラーが発生しました: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
      setIsExporting(false);
    }
  }, [currentProject?.name]);

  // 入力値の変更ハンドラ
  const handlePreImplementationMonthlyCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPreImplementationMonthlyCost(e.target.value));
  };

  const handlePostImplementationMonthlyCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPostImplementationMonthlyCost(e.target.value));
  };

  const handlePricePerMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      setPersonPriceMonthPrice({
        value: e.target.value,
        total_work_loads: totalWorkload,
      }),
    );
  };

  const calculatedInitialCost =
    totalWorkload && personPriceMonthPriceYen
      ? totalWorkload * personPriceMonthPriceYen
      : initialCostYen;

  const { breakEvenMonth, data } = calculateBreakEvenMonthsAndData(
    preImplementationMonthlyCostYen,
    postImplementationMonthlyCostYen,
    calculatedInitialCost,
  );

  const handleCancel = () => {
    dispatch(cancelEdit({ currentProject }));
  };

  const handleToggleEditMode = async () => {
    if (isEditing) {
      if (
        isPreImplementationMonthlyCostInvalid ||
        isPostImplementationMonthlyCostInvalid ||
        isPricePerMonthInvalid
      ) {
        toast.error(TOAST_MESSAGES.COST_EFFECTIVENESS.INVALID_INPUT);
        return;
      }
      await UpdateMonthlyCosts();
    }
    dispatch(toggleEditMode());
  };

  // Step completion states (derived from redux state)
  const step1Complete =
    preImplementationMonthlyCostYen > 0 && !isPreImplementationMonthlyCostInvalid;
  const step2Complete =
    postImplementationMonthlyCostYen > 0 && !isPostImplementationMonthlyCostInvalid;
  const step3Complete = personPriceMonthPriceYen > 0 && !isPricePerMonthInvalid;
  const allStepsComplete = step1Complete && step2Complete && step3Complete;

  // Determine which step to highlight for first-time users
  useEffect(() => {
    if (isFirstTimeUser) {
      if (!step1Complete) {
        setHighlightedStep(1);
      } else if (!step2Complete) {
        setHighlightedStep(2);
      } else if (!step3Complete) {
        setHighlightedStep(3);
      } else {
        setHighlightedStep(null);
        setIsFirstTimeUser(false); // No longer first time if all complete
      }
    }
  }, [isFirstTimeUser, step1Complete, step2Complete, step3Complete]);

  const handleBackClick = () => {
    navigate(-1);
  };

  // コンテナサイズを監視してチャートサイズを更新
  useEffect(() => {
    const updateChartSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        // パディングやマージンを考慮してサイズを調整
        const newWidth = Math.min(containerWidth - 40, 1400);
        const newHeight = Math.min(Math.floor(newWidth * 0.4), 500);
        setChartSize({ width: newWidth, height: newHeight });
      }
    };

    updateChartSize();
    window.addEventListener("resize", updateChartSize);
    return () => window.removeEventListener("resize", updateChartSize);
  }, []);

  const isPageLoading = isDataLoading || isSliceLoading;

  if (isPageLoading) {
    return (
      <div className={styles["container"]}>
        <CommonDocumentHeader
          title="費用対効果グラフ"
          documentType="cost-effectiveness"
          createdAt={estimationInfo?.created_at || ""}
          modifiedAt={estimationInfo?.modified_at || ""}
          isEditable={isEditing}
          isLoading={true}
          handleCancel={handleCancel}
          toggleEditMode={handleToggleEditMode}
          handleHistoryClick={() => {
            const pathSegments = window.location.pathname.split("/");
            pathSegments.pop();
            navigate(`${pathSegments.join("/")}/history`);
          }}
          showEditButton={true}
          showExportButton={false}
          isHistory={false}
          handleBackClick={handleBackClick}
        />
      </div>
    );
  }

  return (
    <div className={styles["container"]}>
      <CommonDocumentHeader
        title="費用対効果グラフ"
        documentType="cost-effectiveness"
        createdAt={estimationInfo?.created_at || ""}
        modifiedAt={estimationInfo?.modified_at || ""}
        isEditable={isEditing}
        isLoading={isPageLoading}
        handleCancel={handleCancel}
        toggleEditMode={handleToggleEditMode}
        handleHistoryClick={() => {
          const pathSegments = window.location.pathname.split("/");
          pathSegments.pop();
          navigate(`${pathSegments.join("/")}/history`);
        }}
        showEditButton={true}
        showExportButton={false}
        isHistory={false}
        handleBackClick={handleBackClick}
      />

      {/* Guidance Header for First-Time Users */}
      {isFirstTimeUser && !allStepsComplete && (
        <div className={styles["inputGuidanceHeader"]}>
          <span className={styles["guidanceIcon"]}>💡</span>
          <div className={styles["guidanceContent"]}>
            <div className={styles["guidanceTitle"]}>費用対効果グラフの作成</div>
            <div className={styles["guidanceDescription"]}>
              現在の運用費用、導入後の予想費用、開発単価を入力して投資回収期間を可視化しましょう
            </div>
          </div>
          <div className={styles["stepIndicator"]}>
            {!step1Complete
              ? "ステップ 1/3"
              : !step2Complete
                ? "ステップ 2/3"
                : !step3Complete
                  ? "ステップ 3/3"
                  : "完了"}
          </div>
        </div>
      )}

      {/* Completion Message */}
      {isFirstTimeUser && allStepsComplete && (
        <div className={styles["completionMessage"]}>
          <span className={styles["checkIcon"]}>✅</span>
          <div className={styles["messageContent"]}>
            <div className={styles["messageTitle"]}>入力完了！</div>
            <div className={styles["messageDescription"]}>
              グラフが生成されました。編集モードで値を調整することができます。
            </div>
          </div>
        </div>
      )}

      {/* ROIグラフ */}
      <div className={styles["graphSection"]}>
        {/* ユーザー入力フォーム */}
        <div className={styles["inputForm"]}>
          {isEditing ? (
            <>
              {/* Step 1: Pre-implementation Cost */}
              <div
                className={`${styles["inputStep"]} ${highlightedStep === 1 ? styles["active"] : ""} ${step1Complete ? styles["completed"] : ""}`}
              >
                <div
                  className={`${styles["stepHeader"]} ${step1Complete ? styles["completed"] : ""}`}
                >
                  <div className={styles["stepNumber"]}>1</div>
                  <div className={styles["stepTitle"]}>導入前の月運用費用</div>
                  <span className={styles["requiredBadge"]}>必須</span>
                </div>
                <div className={styles["stepContent"]}>
                  <div className={styles["formRow"]}>
                    <div className={styles["formGroup"]}>
                      <label>月額運用費用（円）</label>
                      <div className={styles["inputFieldWrapper"]}>
                        <input
                          type="text"
                          value={preImplementationMonthlyCostText}
                          onChange={handlePreImplementationMonthlyCostChange}
                          required
                          className={`${styles["inputField"]} ${styles["inputFieldWithIcon"]} ${highlightedStep === 1 ? styles["highlightedField"] : ""}`}
                          placeholder="例: 1000000"
                        />
                        <span className={styles["inputTooltip"]}>?</span>
                        <div className={styles["tooltipContent"]}>
                          現在の手作業やシステムの月額運用費用を入力
                        </div>
                      </div>
                      {isPreImplementationMonthlyCostInvalid && (
                        <p style={{ color: "red", marginLeft: "4px", fontWeight: "bold" }}>
                          半角数字で入力してください。
                        </p>
                      )}
                    </div>
                  </div>
                  {isFirstTimeUser && highlightedStep === 1 && (
                    <div className={styles["helpText"]}>
                      <span>
                        現在の業務にかかっている月額コストを入力してください（人件費、システム費用など）
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {/* Step 2: Post-implementation Cost */}
              <div
                className={`${styles["inputStep"]} ${highlightedStep === 2 ? styles["active"] : ""} ${step2Complete ? styles["completed"] : ""}`}
              >
                <div
                  className={`${styles["stepHeader"]} ${step2Complete ? styles["completed"] : ""}`}
                >
                  <div className={styles["stepNumber"]}>2</div>
                  <div className={styles["stepTitle"]}>導入後の月運用費用</div>
                  <span className={styles["requiredBadge"]}>必須</span>
                </div>
                <div className={styles["stepContent"]}>
                  <div className={styles["formRow"]}>
                    <div className={styles["formGroup"]}>
                      <label>月額運用費用（円）</label>
                      <div className={styles["inputFieldWrapper"]}>
                        <input
                          type="text"
                          value={postImplementationMonthlyCostText}
                          onChange={handlePostImplementationMonthlyCostChange}
                          required
                          className={`${styles["inputField"]} ${styles["inputFieldWithIcon"]} ${highlightedStep === 2 ? styles["highlightedField"] : ""}`}
                          placeholder="例: 500000"
                        />
                        <span className={styles["inputTooltip"]}>?</span>
                        <div className={styles["tooltipContent"]}>
                          システム導入後の予想月額運用費用を入力
                        </div>
                      </div>
                      {isPostImplementationMonthlyCostInvalid && (
                        <p style={{ color: "red", marginLeft: "4px", fontWeight: "bold" }}>
                          半角数字で入力してください。
                        </p>
                      )}
                    </div>
                  </div>
                  {isFirstTimeUser && highlightedStep === 2 && (
                    <div className={styles["helpText"]}>
                      <span>
                        システム導入後の予想される月額コストを入力してください（削減後の人件費、新システムの運用費など）
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Step 3: Initial Investment Cost */}
              <div
                className={`${styles["inputStep"]} ${highlightedStep === 3 ? styles["active"] : ""} ${step3Complete ? styles["completed"] : ""}`}
              >
                <div
                  className={`${styles["stepHeader"]} ${step3Complete ? styles["completed"] : ""}`}
                >
                  <div className={styles["stepNumber"]}>3</div>
                  <div className={styles["stepTitle"]}>初期投資費用の設定</div>
                  <span className={styles["requiredBadge"]}>必須</span>
                </div>
                <div className={styles["stepContent"]}>
                  <div className={styles["formRow"]}>
                    <div className={styles["formGroup"]}>
                      <label>開発工数</label>
                      <input
                        type="text"
                        value={`${totalWorkload || 0} 人月`}
                        className={`${styles["inputField"]} ${styles["readOnly"]}`}
                        readOnly
                      />
                    </div>
                    <div className={styles["formGroup"]}>
                      <label>人月単価（円）</label>
                      <div className={styles["inputFieldWrapper"]}>
                        <input
                          type="text"
                          value={personPriceMonthPriceText}
                          onChange={handlePricePerMonthChange}
                          required
                          className={`${styles["inputField"]} ${styles["inputFieldWithIcon"]} ${highlightedStep === 3 ? styles["highlightedField"] : ""}`}
                          placeholder="例: 1000000"
                        />
                      </div>
                      {isPricePerMonthInvalid && (
                        <p style={{ color: "red", marginLeft: "4px", fontWeight: "bold" }}>
                          半角数字で入力してください。
                        </p>
                      )}
                    </div>
                    <div className={styles["formGroup"]}>
                      <label>初期費用（円）</label>
                      <input
                        type="text"
                        value={calculatedInitialCost.toLocaleString()}
                        className={`${styles["inputField"]} ${styles["readOnly"]}`}
                        readOnly
                      />
                    </div>
                  </div>
                  {isFirstTimeUser && highlightedStep === 3 && (
                    <div className={styles["helpText"]}>
                      <span>
                        エンジニアの人月単価を入力すると、見積もり工数から初期費用が自動計算されます
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <div className={styles["formRow"]}>
                  <div className={styles["formGroup"]}>
                    <label>導入前の月運用費用（円）</label>
                    <label className={styles["costEffectivenessLabel"]}>
                      {`${preImplementationMonthlyCostYen.toLocaleString()} 円`}
                    </label>
                  </div>
                  <div className={styles["formGroup"]}>
                    <label>導入後の月運用費用（円）</label>
                    <label className={styles["costEffectivenessLabel"]}>
                      {`${postImplementationMonthlyCostYen.toLocaleString()} 円`}
                    </label>
                  </div>
                </div>
              </div>
              <hr className={styles["formSeparator"]} />
              <div className={styles["formRow"]}>
                <div className={styles["formGroup"]}>
                  <div className={styles["costEffectivenessInitialCostContainer"]}>
                    <div>
                      <label className={styles["formSubLabel"]}>開発工数</label>
                      <label className={styles["costEffectivenessLabel"]}>
                        {`${totalWorkload} 人月`}
                      </label>
                    </div>
                    <label
                      className={styles["costEffectivenessLabel"]}
                      style={{ alignSelf: "flex-end" }}
                    >
                      ×
                    </label>
                    <div>
                      <label className={styles["formSubLabel"]}>人月単価</label>
                      <label className={styles["costEffectivenessLabel"]}>
                        {`${personPriceMonthPriceYen.toLocaleString()} 円`}
                      </label>
                    </div>
                    <label
                      className={styles["costEffectivenessLabel"]}
                      style={{ alignSelf: "flex-end" }}
                    >
                      =
                    </label>
                    <div>
                      <label className={styles["formSubLabel"]}>導入時の初期費用</label>
                      <label className={styles["costEffectivenessLabel"]}>
                        {`${calculatedInitialCost.toLocaleString()} 円`}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles["graphContainer"]} ref={containerRef}>
          <div className={styles["breakEvenInfo"]}>
            <div className={styles["breakEvenInfoContent"]}>
              <div>
                <p>
                  損益分岐点: <span className={styles["breakEvenPoint"]}>{breakEvenMonth}</span>
                </p>
                <p className={styles["graphDescription"]}>
                  導入前と導入後の累積運用費用の差（累積効果）を表示しています。
                  累積効果は導入時には初期費用の分だけマイナスからスタートし、損益分岐点でプラスに転じます。
                </p>
              </div>
              <button
                className={styles["exportButton"]}
                onClick={handleDownload}
                disabled={isExporting || isEditing}
                title={isEditing ? "編集モードでは利用できません" : "グラフをPNG形式でエクスポート"}
              >
                <ExportPrimary className={styles["exportIcon"]} />
              </button>
            </div>
          </div>
          {/* レスポンシブチャート */}
          <div ref={chartRef} style={{ width: "100%" }}>
            <ResponsiveContainer width="100%" height={chartSize.height}>
              <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="period"
                  label={{ value: "期間", position: "insideBottomRight", offset: -10 }}
                />
                <YAxis
                  label={{ value: "金額（万円）", angle: -90, position: "insideLeft" }}
                  tickFormatter={(tick) => `${(tick / 10000).toLocaleString()}`}
                />
                <Tooltip
                  formatter={(value, name) => {
                    const nameLabels: Record<string, string> = {
                      cumulativeCostAfter: "導入後の累積運用費用",
                      cumulativeCostBefore: "導入前の累積運用費用",
                      cumulativeEffect: "累積効果",
                    };
                    const formattedName = (name && nameLabels[name]) || name || "";
                    const formattedValue =
                      typeof value === "number" ? value.toLocaleString() : String(value ?? "");
                    return [`${formattedValue} 円`, formattedName];
                  }}
                />
                <Legend
                  formatter={(value: string) => {
                    const legendLabels: Record<string, string> = {
                      cumulativeCostAfter: "導入後の累積運用費用",
                      cumulativeCostBefore: "導入前の累積運用費用",
                      cumulativeEffect: "累積効果",
                    };
                    return legendLabels[value] || value;
                  }}
                />

                {/* 累積効果のエリア表示 */}
                <Area
                  type="monotone"
                  dataKey="cumulativeEffect"
                  fill="#8884d8"
                  stroke="#8884d8"
                  name="cumulativeEffect"
                />

                {/* 導入前後の累積運用費用の折れ線 */}
                <Line
                  type="monotone"
                  dataKey="cumulativeCostBefore"
                  stroke="#e53935"
                  name="cumulativeCostBefore"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 8 }}
                />
                <Line
                  type="monotone"
                  dataKey="cumulativeCostAfter"
                  stroke="#43a047"
                  name="cumulativeCostAfter"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 8 }}
                />

                {/* 損益分岐点の参照線 */}
                {breakEvenMonth !== "60ヶ月以上" && (
                  <ReferenceLine
                    x={breakEvenMonth}
                    stroke="#9c27b0"
                    strokeDasharray="3 3"
                    label={{ value: "損益分岐点", position: "top", fill: "#9c27b0" }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostEffectiveness;
